import React, { useState, useEffect } from 'react';
import { ToggleSwitch, CollapseTransition } from '../../sdk';

/**
 * Tags/meta editor component with toggle and reordering
 * @param {Object} props
 * @param {string[]} props.tags - Current tags array
 * @param {boolean} props.tagsEnabled - Whether tags are shown on slide
 * @param {function} props.onChange - Callback when tags change
 * @param {function} props.onToggle - Callback when enabled state changes
 * @param {number} props.maxTags - Maximum number of tags (default: 6)
 * @param {number} props.slideIndex - Current slide index for syncing state
 */
export function TagsEditor({ tags = [], tagsEnabled = true, onChange, onToggle, maxTags = 6, slideIndex, children }) {
  const [tagInput, setTagInput] = useState('');

  const handleToggle = (checked) => {
    onToggle(checked);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (tags.includes(t)) {
      setTagInput('');
      return;
    }
    onChange([...tags, t].slice(0, maxTags));
    setTagInput('');
  };

  const removeTag = (index) => {
    const updated = [...tags];
    updated.splice(index, 1);
    onChange(updated);
  };

  const moveTag = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tags.length) return;
    
    const updated = [...tags];
    const [removed] = updated.splice(index, 1);
    updated.splice(newIndex, 0, removed);
    onChange(updated);
  };

  return (
    <div className="field-container">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="module-header">Tags</div>
          <div className="module-desc">Up to {maxTags} tags.</div>
        </div>
        <ToggleSwitch
          checked={tagsEnabled}
          onChange={(checked) => handleToggle(checked)}
          size="sm"
        />
      </div>

      <CollapseTransition open={tagsEnabled}>
        <>
          <div className="mt-3 flex gap-2">
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              className="input-base"
              placeholder="Add a tag"
            />
            <button onClick={addTag} className="btn-control">
              Add
            </button>
          </div>

          <div className="mt-3 space-y-2">
            {tags.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="tag-pill">
                  {t}
                </div>
                <button
                  onClick={() => moveTag(i, -1)}
                  disabled={i === 0}
                  className="icon-btn"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveTag(i, 1)}
                  disabled={i === tags.length - 1}
                  className="icon-btn"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  onClick={() => removeTag(i)}
                  className="icon-btn"
                  title="Remove tag"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {children}
        </>
      </CollapseTransition>
    </div>
  );
}
